import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface SuburbDataRow {
  salCode: string;
  name: string;
  state: string;
  areaKm2: number | null;
  lga: string | null;
  postcode: string | null;
  regionId: string | null;
  covered: boolean;
  /** The sub-regions it is in (#260). */
  subRegions?: string[];
  page: string | null;
  score: number | null;
  bucket: string | null;
  population: number | null;
  venueCount: number | null;
  landmarkCount: number | null;
  imageCount: number | null;
  wikiTier: string | null;
}

export interface SuburbDataQuery {
  q?: string | null;
  state?: string | null;
  region?: string | null;
  /** A sub-region id, or 'none' (in a region, in none of its sub-regions). */
  subRegion?: string | null;
  status?: string | null;
  bucket?: string | null;
  sort?: string;
  dir?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
}

export interface SuburbFunnel {
  dataset: { id: number; generatedAt: string } | null;
  suburbs: number;
  withData: number;
  assigned: number;
  covered: number;
  withPage: number;
  coveredByBucket: Record<string, number>;
  candidatesByBucket: Record<string, number>;
}

/** One item of our own data about a suburb: internal (for the team) or public (written for the website). */
export interface SuburbNote {
  id: number;
  visibility: 'internal' | 'public';
  title: string | null;
  body: string;
  source: string | null;
  sourceUrl: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export type SuburbNoteInput = Partial<Pick<SuburbNote, 'visibility' | 'title' | 'body' | 'source' | 'sourceUrl'>>;

/**
 * Suburb data (conductor-server #256): every ABS suburb with its region, coverage, page and crawl data (read-only),
 * and our own notes on a suburb.
 */
@Injectable({ providedIn: 'root' })
export class SuburbDataService {
  private readonly paths = environment.api.paths.api.suburbData;

  constructor(private http: HttpClient) {}

  private params(q: Record<string, any>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') out[k] = String(v);
    return out;
  }

  list(q: SuburbDataQuery): Observable<{ total: number; rows: SuburbDataRow[]; dataset: SuburbFunnel['dataset'] }> {
    return this.http.get<any>(environment.api.host + this.paths.list, { params: this.params(q) }).pipe(map((r) => r.data));
  }

  funnel(q: Pick<SuburbDataQuery, 'q' | 'state' | 'region'>): Observable<SuburbFunnel> {
    return this.http.get<any>(environment.api.host + this.paths.funnel, { params: this.params(q) }).pipe(map((r) => r.data));
  }

  detail(salCode: string): Observable<any> {
    return this.http.get<any>(this.oneUrl(salCode)).pipe(map((r) => r.data));
  }

  private oneUrl(salCode: string): string {
    return environment.api.host + this.paths.one.replace(':salCode', encodeURIComponent(salCode));
  }

  notes(salCode: string): Observable<SuburbNote[]> {
    return this.http.get<any>(`${this.oneUrl(salCode)}/notes`).pipe(map((r) => r.data.notes));
  }

  addNote(salCode: string, note: SuburbNoteInput): Observable<SuburbNote> {
    return this.http.post<any>(`${this.oneUrl(salCode)}/notes`, note).pipe(map((r) => r.data.note));
  }

  updateNote(salCode: string, id: number, note: SuburbNoteInput): Observable<SuburbNote> {
    return this.http.patch<any>(`${this.oneUrl(salCode)}/notes/${id}`, note).pipe(map((r) => r.data.note));
  }

  deleteNote(salCode: string, id: number): Observable<unknown> {
    return this.http.delete<any>(`${this.oneUrl(salCode)}/notes/${id}`);
  }

  // Lead and bio drafts (#254): written by the drafts CLI, decided here.

  drafts(salCode: string): Observable<SuburbDraft[]> {
    return this.http.get<any>(`${this.oneUrl(salCode)}/drafts`).pipe(map((r) => r.data.drafts));
  }

  /** Accept a draft (as edited): the lead becomes the suburb page's intro, the bio its body. */
  acceptDraft(id: number, body: { lead: string | null; bio: string | null; use: { lead: boolean; bio: boolean } }): Observable<{ intro?: string; body?: string }> {
    return this.http.post<any>(`${environment.api.host}${this.paths.list}/drafts/${id}/accept`, body).pipe(map((r) => r.data));
  }

  discardDraft(id: number): Observable<unknown> {
    return this.http.post<any>(`${environment.api.host}${this.paths.list}/drafts/${id}/discard`, {});
  }
}

export interface SuburbDraft {
  id: number;
  lead: string | null;
  bio: string | null;
  checks: { lead: { pass: boolean; longestRun: number } | null; bio: { pass: boolean; longestRun: number } | null } | null;
  model: string;
  generatedAt: string;
  status: 'draft' | 'accepted' | 'discarded';
  decidedAt: string | null;
  decidedBy: string | null;
}
